import type {
  Resource,
  Patient,
  DiagnosticReport,
  DocumentReference,
  Provenance,
  RequestGroup,
} from '@medplum/fhirtypes'
import {
  IconAlertTriangle,
  IconAlertTriangleFilled,
  IconCalendarEvent,
  IconCalendarEventFilled,
  IconClipboardPlus,
  IconClipboardPlusFilled,
  IconClipboardText,
  IconClipboardTextFilled,
  IconFileDescription,
  IconFileDescriptionFilled,
  IconFileInvoice,
  IconFileInvoiceFilled,
  IconFlask,
  IconFlaskFilled,
  IconFolder,
  IconFolderFilled,
  IconFriends,
  IconHeartbeat,
  IconListCheck,
  IconMedicalCross,
  IconMedicalCrossFilled,
  IconMicroscope,
  IconMicroscopeFilled,
  IconPill,
  IconPillFilled,
  IconShield,
  IconShieldCheck,
  IconShieldCheckFilled,
  IconShieldFilled,
  IconTimelineEvent,
  IconTimelineEventFilled,
  IconUser,
  IconUserFilled,
  IconVaccine,
  IconClipboard,
  IconClipboardFilled,
  IconLicense,
} from '@tabler/icons-react'
import type { JSX } from 'react'
import HeartBeatFilled from '/icons/heartbeat-filled.svg'
import VaccineFilled from '/icons/vaccine-filled.svg'
import Scalpel from '/icons/scalpel.svg'
import ScalpelFilled from '/icons/scalpel-filled.svg'
import FriendsFilled from '/icons/friend-filled.svg'
import UserQuestion from '/icons/user-question.svg'
import UserQuestionFilled from '/icons/user-question-filled.svg'

import { formatDateTime, formatListDate } from '@/utils.ts'
import {
  LAB_REQUISITION_DOC_TYPE_CODE,
  OVERVIEW_DOC_TYPE_CODE,
  OVERVIEW_DOC_TYPE_SYSTEM,
  RISKS_GAPS_DOC_TYPE_CODE,
  SCRIBE_DRAFT_DOC_TYPE_CODE,
  KATE_TRANSCRIPT_DOC_TYPE_CODE,
  SCRIBE_TRANSCRIPT_DOC_TYPE_CODE,
} from '@/model/middleware-types'
import { Demographics } from './Demographics'
import { FamilyHistory } from './FamilyHistory.tsx'
import { Procedures } from './Procedures.tsx'
import { Vitals } from './Vitals.tsx'
import { Diagnoses } from './Diagnoses.tsx'
import { Medications } from './Medications.tsx'
import { Allergies } from './Allergies.tsx'
import { Immunizations } from './Immunizations.tsx'
import { SocialHistory } from './SocialHistory.tsx'
import { ListLayout } from './ListLayout.tsx'
import { NewEncounterChartAction } from './NewEncounterChartAction.tsx'
import { LabResults } from './LabResults.tsx'
import { Documents } from './Documents.tsx'
import { Orders } from './Orders.tsx'
import { Encounters } from './Encounters.tsx'
import { Insurances } from './Insurances.tsx'
import { ClinicalSummary } from './ClinicalSummary'
import { RisksGaps } from './RisksGaps'
import { ChronologicalRecords } from './ChronologicalRecords.tsx'
import { ConsentDocuments } from './ConsentDocuments.tsx'
import { OrderTests } from './OrderTests'
import { CareCoordinationNotes } from './CareCoordinationNotes'
import { CareCoordinationTasks } from './CareCoordinationTasks/CareCoordinationTasks'
import { isScribeBackedProvenance } from './ProgressNoteViewer/scribeDetection'
import { ChartAudit } from './Audit/ChartAudit'

export type FormItem = {
  name: string
  label: string
  valueSet?: string
  required?: boolean
}

type ComponentProps = {
  patient: Patient
  item: ChartItem
  refetchPatient: () => void
}

type DisplayData = {
  title: string | JSX.Element
  description: string | JSX.Element
  date: string
  /**
   * When `true`, the left-rail row renders an inline indigo "AI" pill next to
   * the source/date row. Currently set only by the Progress Notes entry when
   * the sibling Provenance carries a `Device/scribe-ai-v1` agent
   * (see `isScribeBackedProvenance`). Mockup parity:
   * `ui-designs/.../ChartPage.tsx:1622`.
   */
  aiBacked?: boolean
  /**
   * When `true`, the left-rail row renders an inline amber "Draft" pill next
   * to the AI pill. Set when the underlying DocumentReference has
   * `docStatus === 'preliminary'` — communicates "unlocked / needs locking"
   * without alarming. Final/locked notes return `false`.
   */
  draftBacked?: boolean
}

export type ChartItem = {
  title: string
  hidden?: boolean
  component: (props: ComponentProps) => JSX.Element | null
  getDisplayData: (resource: Resource, provenance?: Provenance) => DisplayData
  sortItems?: (resources: Resource[]) => Resource[]
  icon?: JSX.Element
  iconOpen?: JSX.Element
  resourceTypes: string[]
  relation: string
  searchFields: string[]
  divide?: boolean
  extraQuery?: string
  countExtraQuery?: string
  formItems?: FormItem[]
  include?: string
  sort?: string
  dates?: string[]
  dataKey: string
}

const defaultDisplayData: DisplayData = {
  title: '',
  description: '',
  date: '',
}

// Newest-first ordering for the DocumentReference-backed left-rail lists.
// Sorts by the same date the row label renders (`date`, falling back to
// `meta.lastUpdated`) so the visible date and the list order always agree.
const sortDocumentsByDateDesc = (resources: Resource[]): Resource[] =>
  resources.sort((a, b) => {
    const dateA = (a as DocumentReference).date || a.meta?.lastUpdated || ''
    const dateB = (b as DocumentReference).date || b.meta?.lastUpdated || ''
    return dateB.localeCompare(dateA)
  })

export const items: ChartItem[] = [
  //// CLINICAL SUMMARY
  {
    title: 'Clinical Summary',
    getDisplayData: () => defaultDisplayData,
    component: ClinicalSummary,
    icon: <IconClipboard width={20} />,
    iconOpen: <IconClipboardFilled width={20} />,
    resourceTypes: [],
    relation: '',
    searchFields: [],
    dataKey: 'clinical-summary',
  },
  //// RISKS & GAPS (AI Clinical Risk Analysis over the chart + guideline corpus, PD-1120)
  {
    title: 'Risks & Gaps',
    getDisplayData: () => defaultDisplayData,
    component: RisksGaps,
    icon: <IconAlertTriangle width={20} />,
    iconOpen: <IconAlertTriangleFilled width={20} />,
    resourceTypes: [],
    relation: '',
    searchFields: [],
    dataKey: 'risks-gaps',
  },
  //// DEMOGRAPHICS
  {
    title: 'Demographics',
    getDisplayData: () => defaultDisplayData,
    component: Demographics,
    icon: <IconUser width={20} />,
    iconOpen: <IconUserFilled width={20} />,
    resourceTypes: [],
    relation: '',
    searchFields: [],
    dataKey: 'demographics',
  },
  //// CARE COORDINATION NOTES
  {
    title: 'Care Coordination Notes',
    component: ({ patient, item, refetchPatient }: ComponentProps) => (
      <CareCoordinationNotes patient={patient} item={item} refetchPatient={refetchPatient} />
    ),
    getDisplayData: () => defaultDisplayData,
    icon: <IconFileDescription width={20} />,
    iconOpen: <IconFileDescriptionFilled width={20} />,
    resourceTypes: ['Communication'],
    relation: 'subject',
    extraQuery: 'category=care-coordination-note',
    searchFields: [],
    dataKey: 'care-coordination-notes',
  },
  //// CARE COORDINATION TASKS (PD-1870 — the patient's Care Queue tasks, open and completed)
  {
    title: 'Care Coordination Tasks',
    // Keyed on the patient: PatientPage reuses the section component across patients,
    // and this one holds filter and selection state (see the Audit entry below).
    component: ({ patient, item, refetchPatient }: ComponentProps) => (
      <CareCoordinationTasks
        key={patient.id}
        patient={patient}
        item={item}
        refetchPatient={refetchPatient}
      />
    ),
    getDisplayData: () => defaultDisplayData,
    icon: <IconListCheck width={20} />,
    iconOpen: <IconListCheck width={20} stroke={2.5} />,
    // Board items are Basic resources served by the middleware, not a FHIR search on
    // the patient — the nav count (open tasks) comes from the section's own query,
    // merged in by PatientPage (PD-1882).
    resourceTypes: [],
    relation: '',
    searchFields: [],
    dataKey: 'care-coordination-tasks',
  },
  //// CHRONOLOGICAL RECORDS
  {
    title: 'Chronological Records',
    component: ChronologicalRecords,
    getDisplayData: () => defaultDisplayData,
    icon: <IconTimelineEvent width={20} />,
    iconOpen: <IconTimelineEventFilled width={20} />,
    resourceTypes: [],
    relation: '',
    searchFields: [],
    dataKey: 'timeline',
  },
  //// PROGRESS NOTES
  {
    title: 'Progress Notes',
    component: ({ patient, item }: ComponentProps) => (
      <ListLayout patient={patient} item={item} action={<NewEncounterChartAction />}>
        <Documents title="Progress Note" />
      </ListLayout>
    ),
    getDisplayData: (resource: Resource, provenance?: Provenance) => {
      const document = resource as DocumentReference
      const description = document.description || ''
      const type = document.type?.text || document.type?.coding?.[0]?.display
      const source = provenance?.agent?.find((p) =>
        p.type?.coding?.some((t) => t.code === 'source'),
      )?.who?.display
      return {
        title: source || description,
        description: `${description}${type ? ` (${type})` : ''}`,
        date: formatListDate(document.date || ''),
        // LD-21 — flag scribe-backed Progress Notes so the left-rail row
        // renders the indigo "AI" pill. The Provenance comes from the same
        // `_revinclude=Provenance:target` already fetched in `ListLayout`,
        // so no extra round-trip is required. Legacy (non-scribe) notes
        // return `false` here and the rail row looks unchanged.
        aiBacked: isScribeBackedProvenance(provenance),
        // Surface preliminary (unlocked / draft) Progress Notes with an
        // amber "Draft" pill so doctors can spot un-locked notes at a glance.
        draftBacked: document.docStatus === 'preliminary',
      }
    },
    icon: <IconFileDescription width={20} />,
    iconOpen: <IconFileDescriptionFilled width={20} />,
    resourceTypes: ['DocumentReference'],
    extraQuery: 'type=ProgressNote',
    countExtraQuery: 'status:not=entered-in-error',
    // The visit's `serviceProvider` decides who may remove a draft note (PD-2031).
    include: 'encounter',
    relation: 'subject',
    searchFields: ['description'],
    sort: 'date',
    sortItems: sortDocumentsByDateDesc,
    dates: ['date'],
    dataKey: 'progress-notes',
  },
  //// ORDER TESTS
  {
    title: 'Order Tests',
    getDisplayData: () => defaultDisplayData,
    component: OrderTests,
    icon: <IconFlask width={20} />,
    iconOpen: <IconFlaskFilled width={20} />,
    resourceTypes: [],
    relation: '',
    searchFields: [],
    dataKey: 'order-tests',
    hidden: true,
  },
  //// ORDERS
  {
    title: 'Orders',
    component: ({ patient, item }: ComponentProps) => (
      <ListLayout patient={patient} item={item}>
        <Orders />
      </ListLayout>
    ),
    getDisplayData: (resource: Resource) => {
      const request = resource as RequestGroup
      const ext = request.extension?.find((item) => item.url.match(/requestgroup-performer/))

      const title = ext?.valueReference?.display || 'Order'
      return {
        title: title,
        date: formatDateTime(request.authoredOn || request.meta?.lastUpdated || ''),
        description: request.status,
      }
    },
    icon: <IconClipboardText width={20} />,
    iconOpen: <IconClipboardTextFilled width={20} />,
    resourceTypes: ['RequestGroup'],
    relation: 'subject',
    searchFields: ['code'],
    countExtraQuery: 'status:not=entered-in-error',
    divide: true,
    sort: 'authored,_lastUpdated',
    dates: ['authored'],
    dataKey: 'orders',
  },
  //// LABS/IMAGING
  {
    title: 'Labs/Imaging',
    component: ({ patient, item }: ComponentProps) => (
      // Keyed on the patient (as Audit is): rows and the PD-1685 selection start fresh for
      // each patient instead of carrying over the previous one's.
      <ListLayout key={patient.id} patient={patient} item={item}>
        <LabResults />
      </ListLayout>
    ),
    getDisplayData: (resource: Resource) => {
      const diagnosticReport = resource as DiagnosticReport

      const abnormalExt = diagnosticReport.extension?.find((item) =>
        item.url.match(/interpretation/),
      )

      let description: string | JSX.Element =
        diagnosticReport.conclusion ||
        diagnosticReport.code?.text ||
        diagnosticReport.code?.coding?.[0]?.display ||
        ''

      if (
        abnormalExt?.valueCodeableConcept?.coding?.some((item) => item.display?.match(/abnormal/i))
      ) {
        description = (
          <>
            <span className="text-red-500">Abn</span>, {description}
          </>
        )
      }
      return {
        title:
          diagnosticReport.performer?.[0].display ||
          diagnosticReport.code?.text ||
          diagnosticReport.code?.coding?.[0]?.display ||
          '',
        description,
        date: formatDateTime(diagnosticReport.effectiveDateTime || diagnosticReport.issued || ''),
      }
    },
    sortItems: (resources) =>
      resources.sort((a, b) => {
        const dateA =
          (a as DiagnosticReport).effectiveDateTime || (a as DiagnosticReport).issued || ''
        const dateB =
          (b as DiagnosticReport).effectiveDateTime || (b as DiagnosticReport).issued || ''
        return dateB.localeCompare(dateA)
      }),
    icon: <IconMicroscope width={20} />,
    iconOpen: <IconMicroscopeFilled width={20} />,
    resourceTypes: ['DiagnosticReport'],
    countExtraQuery: 'status:not=entered-in-error',
    relation: 'subject',
    searchFields: ['code'],
    sort: 'effectiveDateTime,issued',
    dates: ['effectiveDateTime', 'issued'],
    dataKey: 'labs-imaging',
  },
  //// DOCUMENTS
  {
    title: 'Documents',
    component: ({ patient, item }: ComponentProps) => (
      // Keyed on the patient (as Audit is): rows and the PD-1685 selection start fresh for
      // each patient instead of carrying over the previous one's.
      <ListLayout key={patient.id} patient={patient} item={item}>
        <Documents title="Document Summary" />
      </ListLayout>
    ),
    getDisplayData: (resource: Resource, provenance?: Provenance) => {
      const document = resource as DocumentReference
      const description = document.description || ''
      const type = document.type?.text || document.type?.coding?.[0]?.display
      const source = provenance?.agent?.find((p) =>
        p.type?.coding?.some((t) => t.code === 'source'),
      )?.who?.display
      return {
        title: source || description,
        description: `${description}${type ? ` (${type})` : ''}`,
        date: formatDateTime(document.date || ''),
      }
    },
    icon: <IconFolder width={20} />,
    iconOpen: <IconFolderFilled width={20} />,
    resourceTypes: ['DocumentReference'],
    // ScribeTranscript and ScribeDraft are internal AI artifacts — physicians
    // read the transcript via the note's accordion and recover a draft from the
    // call rail, never as standalone chart documents (PD-1138).
    // The Overview doc type is a generated Clinical Summary, also hidden (PD-1049),
    // as is the generated Risks & Gaps report (PD-1120).
    // A patient-carried requisition is stamped LabRequisition rather than Order so the
    // patient portal can surface it (PD-1312); in the chart it is still the order's own
    // PDF, already rendered inside the Orders entry, so listing it here would show the
    // same order twice (PD-1745).
    extraQuery: `type:not=ProgressNote&type:not=HistoryPhysical&type:not=Order&type:not=${LAB_REQUISITION_DOC_TYPE_CODE}&type:not=${SCRIBE_TRANSCRIPT_DOC_TYPE_CODE}&type:not=${SCRIBE_DRAFT_DOC_TYPE_CODE}&type:not=${KATE_TRANSCRIPT_DOC_TYPE_CODE}&type:not=${encodeURIComponent(`${OVERVIEW_DOC_TYPE_SYSTEM}|${OVERVIEW_DOC_TYPE_CODE}`)}&type:not=${encodeURIComponent(`${OVERVIEW_DOC_TYPE_SYSTEM}|${RISKS_GAPS_DOC_TYPE_CODE}`)}`,
    countExtraQuery: 'status:not=entered-in-error',
    relation: 'subject',
    searchFields: ['description'],
    sort: 'date',
    sortItems: sortDocumentsByDateDesc,
    dates: ['date'],
    dataKey: 'documents',
  },
  {
    hidden: true,
    title: 'Consent Documents',
    component: ({ patient, item }: ComponentProps) => (
      <ListLayout patient={patient} item={item}>
        <ConsentDocuments title="Consent Documents" />
      </ListLayout>
    ),
    getDisplayData: (resource: Resource, provenance?: Provenance) => {
      const document = resource as DocumentReference
      const description = document.description || ''
      const type = document.type?.text || document.type?.coding?.[0]?.display
      const source = provenance?.agent?.find((p) =>
        p.type?.coding?.some((t) => t.code === 'source'),
      )?.who?.display
      return {
        title: source || description,
        description: `${description}${type ? ` (${type})` : ''}`,
        date: formatDateTime(document.date || ''),
      }
    },
    icon: <IconLicense width={20} />,
    iconOpen: <IconLicense width={20} />,
    resourceTypes: ['DocumentReference'],
    extraQuery: '',
    relation: 'patient',
    searchFields: ['description'],
    sort: 'date',
    sortItems: sortDocumentsByDateDesc,
    dates: ['date'],
    dataKey: 'consents',
  },
  //// VITALS
  {
    title: 'Vitals',
    component: Vitals,
    icon: <IconHeartbeat width={20} />,
    getDisplayData: () => defaultDisplayData,
    iconOpen: <img src={HeartBeatFilled} alt="heartbeat" width={20} />,
    resourceTypes: ['Observation'],
    relation: 'subject',
    searchFields: ['code', 'category', 'status'],
    extraQuery: 'category=vital-signs&code=85353-1&_include=Observation:has-member',
    dataKey: 'vitals',
    formItems: [
      {
        name: 'code',
        label: 'Vital Sign',
        valueSet: 'http://cts.nlm.nih.gov/fhir/ValueSet/2.16.840.1.113883.3.88.12.80.62',
        required: true,
      },
      { name: 'valueQuantity', label: 'Value', required: true },
      { name: 'effectiveDateTime', label: 'Date/Time', required: true },
      {
        name: 'status',
        label: 'Status',
        valueSet: 'http://hl7.org/fhir/ValueSet/observation-status',
        required: true,
      },
    ],
    sort: '-date',
    dates: ['effectiveDateTime', 'effectiveInstant', 'issued', 'valueDateTime'],
  },
  //// HISTORY AND PHYSICAL
  {
    title: 'History and Physical',
    component: ({ patient, item }: ComponentProps) => (
      <ListLayout patient={patient} item={item}>
        <Documents title="Document Summary" />
      </ListLayout>
    ),
    getDisplayData: (resource: Resource, provenance?: Provenance) => {
      const document = resource as DocumentReference
      const description = document.description || ''
      const type = document.type?.text || document.type?.coding?.[0]?.display
      const source = provenance?.agent?.find((p) =>
        p.type?.coding?.some((t) => t.code === 'source'),
      )?.who?.display
      return {
        title: source || description,
        description: `${description}${type ? ` (${type})` : ''}`,
        date: formatDateTime(document.date || ''),
      }
    },
    icon: <IconClipboardPlus width={20} />,
    iconOpen: <IconClipboardPlusFilled width={20} />,
    resourceTypes: ['DocumentReference'],
    extraQuery: 'type=HistoryPhysical',
    countExtraQuery: 'status:not=entered-in-error',
    relation: 'subject',
    searchFields: ['description'],
    sort: 'date',
    sortItems: sortDocumentsByDateDesc,
    dates: ['date'],
    dataKey: 'history-physical',
  },
  //// INSURANCES
  {
    title: 'Insurances',
    component: Insurances,
    getDisplayData: () => defaultDisplayData,
    icon: <IconShield width={20} />,
    iconOpen: <IconShieldFilled width={20} />,
    resourceTypes: [],
    relation: '',
    searchFields: ['type', 'status'],
    dataKey: 'insurances',
    formItems: [
      {
        name: 'type',
        label: 'Coverage Type',
        valueSet: 'http://hl7.org/fhir/ValueSet/coverage-type',
        required: true,
      },
      {
        name: 'status',
        label: 'Status',
        valueSet: 'http://hl7.org/fhir/ValueSet/fm-status',
        required: true,
      },
      { name: 'payor', label: 'Payor', required: true },
      { name: 'subscriberId', label: 'Subscriber ID' },
    ],
  },
  //// DIAGNOSIS
  {
    title: 'Diagnosis',
    component: Diagnoses,
    getDisplayData: () => defaultDisplayData,
    icon: <IconFileInvoice width={20} />,
    iconOpen: <IconFileInvoiceFilled width={20} />,
    resourceTypes: ['Condition'],
    relation: 'subject',
    countExtraQuery: 'category:not=Problem%20HL7.CCDAR2',
    searchFields: ['code', 'category', 'clinicalStatus'],
    divide: true,
    dataKey: 'diagnosis',
    formItems: [
      {
        name: 'code',
        label: 'Condition',
        valueSet: 'http://cts.nlm.nih.gov/fhir/ValueSet/2.16.840.1.113883.3.88.12.3221.7.4',
        required: true,
      },
      {
        name: 'category',
        label: 'Category',
        valueSet: 'http://hl7.org/fhir/ValueSet/condition-category',
      },
      {
        name: 'clinicalStatus',
        label: 'Clinical Status',
        valueSet: 'http://hl7.org/fhir/ValueSet/condition-clinical',
      },
      {
        name: 'verificationStatus',
        label: 'Verification Status',
        valueSet: 'http://hl7.org/fhir/ValueSet/condition-ver-status',
      },
      { name: 'onsetDateTime', label: 'Onset Date' },
    ],
    sort: '-recorded-date,-asserted-date',
    dates: ['onsetDateTime', 'onsetString', 'recordedDate', 'abatementDateTime'],
  },
  //// MEDICATIONS
  {
    title: 'Medications',
    component: Medications,
    getDisplayData: () => defaultDisplayData,
    icon: <IconPill width={20} />,
    iconOpen: <IconPillFilled width={20} />,
    resourceTypes: ['MedicationStatement'],
    relation: 'subject',
    searchFields: ['code', 'category', 'status'],
    extraQuery: '_include=MedicationStatement:medication',
    dataKey: 'medications',
    formItems: [
      {
        name: 'medicationCodeableConcept',
        label: 'Medication',
        valueSet: 'http://cts.nlm.nih.gov/fhir/ValueSet/2.16.840.1.113762.1.4.1010.4',
        required: true,
      },
      {
        name: 'status',
        label: 'Status',
        valueSet: 'http://hl7.org/fhir/ValueSet/medicationrequest-status',
        required: true,
      },
      { name: 'dosage', label: 'Dosage Instructions' },
    ],
    sort: '-effective',
    dates: ['effectiveDateTime', 'dateAsserted'],
  },
  //// ALLERGIES
  {
    title: 'Allergies',
    component: Allergies,
    getDisplayData: () => defaultDisplayData,
    icon: <IconMedicalCross width={20} />,
    iconOpen: <IconMedicalCrossFilled width={20} />,
    resourceTypes: ['AllergyIntolerance'],
    relation: 'patient',
    searchFields: ['code', 'category', 'clinicalStatus'],
    dataKey: 'allergies',
    formItems: [
      {
        name: 'code',
        label: 'Allergen',
        valueSet: 'http://cts.nlm.nih.gov/fhir/ValueSet/2.16.840.1.113762.1.4.1186.8',
        required: true,
      },
      {
        name: 'clinicalStatus',
        label: 'Clinical Status',
        valueSet: 'http://hl7.org/fhir/ValueSet/allergyintolerance-clinical',
      },
      { name: 'reaction', label: 'Reaction' },
    ],
  },
  //// IMMUNIZATIONS
  {
    title: 'Immunizations',
    component: Immunizations,
    getDisplayData: () => defaultDisplayData,
    icon: <IconVaccine width={20} />,
    iconOpen: <img src={VaccineFilled} alt="vaccine-filled" width={20} />,
    resourceTypes: ['Immunization'],
    relation: 'patient',
    searchFields: ['code', 'category', 'status'],
    dataKey: 'immunizations',
    formItems: [
      {
        name: 'vaccineCode',
        label: 'Vaccine',
        valueSet: 'http://cts.nlm.nih.gov/fhir/ValueSet/2.16.840.1.113883.10.20.5.9.10',
        required: true,
      },
      {
        name: 'status',
        label: 'Status',
        valueSet: 'http://hl7.org/fhir/ValueSet/immunization-status',
        required: true,
      },
      { name: 'occurrenceDateTime', label: 'Date Administered', required: true },
      { name: 'lotNumber', label: 'Lot Number' },
      { name: 'isSubpotent', label: 'Subpotent?' },
    ],
    sort: '-date',
    dates: ['recorded', 'occurrenceDateTime'],
  },
  //// PROCEDURES
  {
    title: 'Procedures',
    component: Procedures,
    getDisplayData: () => defaultDisplayData,
    icon: <img src={Scalpel} alt="scalpel" width={20} />,
    iconOpen: <img src={ScalpelFilled} alt="scalpel-filled" width={20} />,
    resourceTypes: ['Procedure'],
    relation: 'subject',
    searchFields: ['code', 'category', 'status'],
    dataKey: 'procedures',
    formItems: [
      {
        name: 'code',
        label: 'Procedure',
        valueSet: 'http://cts.nlm.nih.gov/fhir/ValueSet/2.16.840.1.113883.3.88.12.80.28',
        required: true,
      },
      {
        name: 'status',
        label: 'Status',
        valueSet: 'http://hl7.org/fhir/ValueSet/event-status',
        required: true,
      },
      { name: 'performedDateTime', label: 'Date Performed' },
    ],
    sort: '-date',
    dates: ['performedDateTime'],
  },
  //// FAMILY HISTORY
  {
    title: 'Family History',
    component: FamilyHistory,
    getDisplayData: () => defaultDisplayData,
    icon: <IconFriends width={20} />,
    iconOpen: <img src={FriendsFilled} alt="friends-filled" width={20} />,
    resourceTypes: ['FamilyMemberHistory'],
    relation: 'patient',
    searchFields: ['code', 'category', 'clinicalStatus'],
    dataKey: 'family-history',
    formItems: [
      {
        name: 'relationship',
        label: 'Relationship',
        valueSet: 'http://terminology.hl7.org/ValueSet/v3-PersonalRelationshipRoleType',
        required: true,
      },
      {
        name: 'condition',
        label: 'Condition',
        valueSet: 'http://cts.nlm.nih.gov/fhir/ValueSet/2.16.840.1.113883.3.464.1003.103.12.1001',
        required: true,
      },
      {
        name: 'sex',
        label: 'Sex',
        valueSet: 'http://cts.nlm.nih.gov/fhir/ValueSet/2.16.840.1.113883.3.2074.1.1.14',
        required: true,
      },
      {
        name: 'status',
        label: 'Status',
        valueSet: 'http://hl7.org/fhir/ValueSet/history-status',
        required: true,
      },
      { name: 'note', label: 'Notes' },
    ],
  },
  //// SOCIAL HISTORY
  {
    title: 'Social History',
    component: SocialHistory,
    getDisplayData: () => defaultDisplayData,
    icon: <img src={UserQuestion} alt="user-filled" width={20} />,
    iconOpen: <img src={UserQuestionFilled} alt="user-question-filled" width={20} />,
    resourceTypes: ['Observation'],
    relation: 'patient',
    extraQuery: 'category=social-history',
    searchFields: [],
    dataKey: 'social-history',
  },
  //// ENCOUNTERS
  {
    title: 'Encounters',
    component: Encounters,
    getDisplayData: () => defaultDisplayData,
    icon: <IconCalendarEvent width={20} />,
    iconOpen: <IconCalendarEventFilled width={20} />,
    resourceTypes: ['Encounter'],
    relation: 'subject',
    // A visit the patient cancelled through Kate or by text stays on the chart as
    // a record (the portal's Cancel deletes it instead); the list hides both, as
    // the patient portal's visit list does.
    extraQuery: 'status:not=cancelled',
    searchFields: ['type', 'category', 'status'],
    sort: '-date',
    dates: ['period.start', 'period.end'],
    dataKey: 'encounters',
  },
  //// AUDIT (PD-1322 — Chart Audit: access + change history)
  {
    title: 'Audit',
    /**
     * Keyed on the patient, deliberately.
     *
     * `PatientPage` renders `<currentItem.component>` with no key, so switching patient
     * while a section is open re-renders that component with new props instead of
     * remounting it. Every section inherits that; for this one it means the reader arrives
     * at patient B's audit trail still holding patient A's filter set — a 7-day window, one
     * named user, emergency-access-only — with nothing on screen saying so. A narrowed
     * audit list that looks unnarrowed is the failure mode this whole tab exists to
     * prevent, so the Audit entry mounts fresh per patient.
     *
     * Scoped to this entry rather than fixed at the render site: cross-patient state is
     * latent in every section here, and fixing it registry-wide is a separate change with
     * a much larger blast radius (filed as its own ticket).
     */
    component: ({ patient }: ComponentProps) => <ChartAudit key={patient.id} patient={patient} />,
    getDisplayData: () => defaultDisplayData,
    icon: <IconShieldCheck width={20} />,
    iconOpen: <IconShieldCheckFilled width={20} />,
    resourceTypes: [],
    relation: '',
    searchFields: [],
    dataKey: 'audit',
  },
]

const itemsMap = new Map<string, ChartItem>()
for (const item of items) {
  itemsMap.set(item.dataKey, item)
}

export { itemsMap }
